// admin/src/services/api.ts
import axios from 'axios';

/* ============================ Base URL ============================ */
const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:4000/api';

/* ============================ Axios ============================ */
export const API = axios.create({
  baseURL: BASE_URL,
  timeout: 20000,
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

API.interceptors.response.use(
  (res) => res,
  (err) => {
    const method = (err?.config?.method || '').toString().toUpperCase();
    const url = err?.config?.url || '';
    const status = err?.response?.status;
    const data = err?.response?.data;
    console.warn('[API ERROR]', method, url, status, data);
    return Promise.reject(err);
  }
);

/* ============================ Helpers ============================ */
const nowTs = () => ({ __ts: Date.now() });

const noCacheHeaders = {
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  Pragma: 'no-cache',
  Expires: '0',
} as const;

const withNoCache = { params: nowTs(), headers: noCacheHeaders };

const unwrapList = <T = any>(data: any, keys: string[]): T[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data as T[];
  for (const k of keys) if (Array.isArray((data as any)?.[k])) return (data as any)[k] as T[];
  for (const k of ['items', 'data', 'users', 'projects', 'assignees', 'statuses', 'paymentTypes', 'results']) {
    if (Array.isArray((data as any)?.[k])) return (data as any)[k] as T[];
  }
  return [];
};

const isFD = (x: any) => typeof FormData !== 'undefined' && x instanceof FormData;

const postOrPut = async (method: 'post' | 'put', url: string, data: any) => {
  const headers = isFD(data)
    ? { 'Content-Type': 'multipart/form-data', ...noCacheHeaders }
    : noCacheHeaders;
  const resp = await API.request({ url, method, data, headers, params: nowTs() });
  return resp.data;
};

/**
 * Try a list of GET endpoints in order. Returns the first successful response.data.
 * If a 401 occurs, it aborts and throws immediately.
 */
async function tryGetInOrder<T = any>(endpoints: string[]): Promise<T> {
  const errors: any[] = [];
  for (const ep of endpoints) {
    try {
      const r = await API.get(ep, withNoCache);
      return r.data as T;
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 401) throw e; // unauthorized => stop trying
      errors.push({ ep, status, data: e?.response?.data });
      // continue to next candidate
    }
  }
  const msg = `Profile fetch failed (${errors.map((x) => `${x.ep}:${x.status || 'ERR'}`).join(', ')})`;
  const err = new Error(msg) as any;
  err.details = errors;
  throw err;
}

/* ============================ NOTES ============================ */
export type FetchNotesOpts = {
  search?: string;
  date?: string;     // yyyy-mm-dd
  userId?: string;   // filter by user (admin only)
  adminAll?: boolean;
  includeUser?: boolean;
};

export function fetchNotes(search?: string, date?: string): Promise<any>;
export function fetchNotes(opts?: FetchNotesOpts): Promise<any>;
export function fetchNotes(a?: any, b?: any) {
  let p: FetchNotesOpts;
  if (typeof a === 'object' && a !== null) {
    p = a;
  } else {
    p = { search: a || '', date: b || '' };
  }

  const params: Record<string, any> = {
    search: p.search || undefined,
    date: p.date || undefined,
    userId: p.userId || undefined,                 // backend respects only for admins
    adminAll: p.adminAll ? 1 : undefined,          // ask backend for ALL users' notes
    includeUser: p.includeUser ? 1 : undefined,    // ask backend to populate user
    ...nowTs(),
  };

  return API.get('/notes', {
    params,
    headers: noCacheHeaders,
  });
}

export const fetchAllNotes = (opts?: Omit<FetchNotesOpts, 'adminAll' | 'includeUser'>) =>
  fetchNotes({ ...opts, adminAll: true, includeUser: true });

export const getNoteById = (id: string) =>
  API.get(`/notes/${id}`, { params: nowTs(), headers: noCacheHeaders }).then((r) => r.data);

export const createNote = (data: any) => postOrPut('post', '/notes', data);
export const updateNote = (id: string, data: any) => postOrPut('put', `/notes/${id}`, data);
export const deleteNote = (id: string) =>
  API.delete(`/notes/${id}`, { params: nowTs(), headers: noCacheHeaders }).then((r) => r.data);

/* ============================ USERS ============================ */
export const fetchUsers = async () => {
  const r = await API.get('/users', { params: nowTs(), headers: noCacheHeaders });
  return r.data;
};

export const getUserById = (id: string) =>
  API.get(`/users/${id}`, { params: nowTs(), headers: noCacheHeaders }).then((r) => r.data);

export const createUser = (data: any) =>
  API.post('/users', data, { params: nowTs(), headers: noCacheHeaders }).then((r) => r.data);

export const updateUser = (id: string, data: any) =>
  API.put(`/users/${id}`, data, { params: nowTs(), headers: noCacheHeaders }).then((r) => r.data);

export const deleteUser = (id: string) =>
  API.delete(`/users/${id}`, { params: nowTs(), headers: noCacheHeaders }).then((r) => r.data);

/* ============================ PROJECTS ============================ */
export const listProjects = async () => {
  const r = await API.get('/projects', { params: nowTs(), headers: noCacheHeaders });
  return Array.isArray(r.data) ? r.data : unwrapList(r.data, ['projects', 'items']);
};

export const listProjectsForUser = async (userId: string) => {
  const r = await API.get(`/admin/users/${userId}/projects`, { params: nowTs(), headers: noCacheHeaders });
  return Array.isArray(r.data) ? r.data : [];
};

export const createProject = async (name: string, opts?: { forUser?: string }) => {
  const body: any = { name };
  if (opts?.forUser) body.user = opts.forUser;
  const r = await API.post('/projects', body, { params: nowTs(), headers: noCacheHeaders });
  return r.data;
};

export const updateProject = (id: string, name: string) =>
  API.put(`/projects/${id}`, { name }, { params: nowTs(), headers: noCacheHeaders }).then((r) => r.data);

export const deleteProject = (id: string) =>
  API.delete(`/projects/${id}`, { params: nowTs(), headers: noCacheHeaders }).then((r) => r.data);

/* ============================ ASSIGNEES ============================ */
export const listAssignees = async () => {
  const r = await API.get('/assignees', { params: nowTs(), headers: noCacheHeaders });
  return unwrapList<{ _id: string; name: string; email?: string; username?: string }>(r.data, ['assignees', 'items']);
};

export const listAssigneesForUser = async (userId: string) => {
  const r = await API.get(`/admin/users/${userId}/assignees`, { params: nowTs(), headers: noCacheHeaders });
  return Array.isArray(r.data) ? r.data : [];
};

export const createAssignee = async (
  payload: { name: string; email?: string; username: string; password: string },
  opts?: { forUser?: string }
) => {
  const body: any = { ...payload };
  if (opts?.forUser) body.user = opts.forUser;
  const r = await API.post('/assignees', body, { params: nowTs(), headers: noCacheHeaders });
  return r.data;
};

export const updateAssignee = (id: string, payload: { name?: string; email?: string; username?: string; password?: string }) =>
  API.put(`/assignees/${id}`, payload, { params: nowTs(), headers: noCacheHeaders }).then((r) => r.data);

export const deleteAssignee = (id: string) =>
  API.delete(`/assignees/${id}`, { params: nowTs(), headers: noCacheHeaders }).then((r) => r.data);

/* ============================ STATUSES ============================ */
export const listStatuses = async () => {
  const r = await API.get('/statuses', { params: nowTs(), headers: noCacheHeaders });
  return unwrapList<{ _id: string; name: string }>(r.data, ['statuses', 'items']);
};

export const listStatusesForUser = async (userId: string) => {
  const r = await API.get(`/admin/users/${userId}/statuses`, { params: nowTs(), headers: noCacheHeaders });
  return Array.isArray(r.data) ? r.data : [];
};

export const createStatus = async (name: string, opts?: { forUser?: string }) => {
  const body: any = { name };
  if (opts?.forUser) body.user = opts.forUser;
  const r = await API.post('/statuses', body, { params: nowTs(), headers: noCacheHeaders });
  return r.data;
};

export const updateStatus = (id: string, name: string) =>
  API.put(`/statuses/${id}`, { name }, { params: nowTs(), headers: noCacheHeaders }).then((r) => r.data);

export const deleteStatus = (id: string) =>
  API.delete(`/statuses/${id}`, { params: nowTs(), headers: noCacheHeaders }).then((r) => r.data);

/* ============================ PAYMENT TYPES ============================ */
export const listPaymentTypes = async () => {
  const r = await API.get('/payment-types', { params: nowTs(), headers: noCacheHeaders });
  return unwrapList<{ _id: string; name: string }>(r.data, ['paymentTypes', 'items']);
};

export const listPaymentTypesForUser = async (userId: string) => {
  const r = await API.get(`/admin/users/${userId}/payment-types`, { params: nowTs(), headers: noCacheHeaders });
  return Array.isArray(r.data) ? r.data : [];
};

export const createPaymentType = async (name: string, opts?: { forUser?: string }) => {
  const body: any = { name };
  if (opts?.forUser) body.user = opts.forUser;
  const r = await API.post('/payment-types', body, { params: nowTs(), headers: noCacheHeaders });
  return r.data;
};

export const updatePaymentType = (id: string, name: string) =>
  API.put(`/payment-types/${id}`, { name }, { params: nowTs(), headers: noCacheHeaders }).then((r) => r.data);

export const deletePaymentType = (id: string) =>
  API.delete(`/payment-types/${id}`, { params: nowTs(), headers: noCacheHeaders }).then((r) => r.data);

/* ============================ Tunes (admin) ============================ */
export interface TuneItem {
  _id: string;
  title: string;
  artist?: string;
  originalName?: string;
  fileUrl: string;
  filename: string;
  size: number;
  mimetype: string;
  createdAt?: string;
  uploadedBy?: { username?: string; email?: string; fullName?: string };
}

export const listTunes = (search = '') =>
  API.get('/tunes', {
    params: { search: search || undefined, ...nowTs() },
    headers: noCacheHeaders,
  }).then((r) => unwrapList<TuneItem>(r.data, ['items', 'tunes']));

/* ============================ Self Profile (non-admin fallback) ============================ */
export const getProfile = () =>
  API.get('/profile/me', withNoCache).then((r) => r.data);

export const updateProfile = (payload: { fullName: string; email: string; mobile: string }) =>
  API.put('/profile/me', payload, withNoCache).then((r) => r.data);

export const changePassword = (payload: { currentPassword: string; newPassword: string }) =>
  API.put('/profile/me/password', payload, withNoCache).then((r) => r.data);

/* ============================ Admin Profile (resilient) ============================ */
export const getAdminProfile = async () => {
  const candidates = [
    '/admin/profile/me', // preferred
    '/profile/me',       // fallback if admin route not present
    '/admin/me',         // legacy patterns
    '/me',
  ];
  return tryGetInOrder<any>(candidates);
};

export const updateAdminProfile = (payload: {
  fullName?: string; username?: string; email?: string; avatar?: File;
}) => {
  const fd = new FormData();
  if (payload.fullName) fd.append('fullName', payload.fullName);
  if (payload.username) fd.append('username', payload.username);
  if (payload.email) fd.append('email', payload.email);
  if (payload.avatar) fd.append('avatar', payload.avatar);

  return API.put('/admin/profile/me', fd, {
    ...withNoCache,
    headers: { 'Content-Type': 'multipart/form-data', ...noCacheHeaders },
  }).then((r) => r.data);
};

export const changeAdminPassword = (payload: { currentPassword: string; newPassword: string }) =>
  API.put('/admin/profile/password', payload, withNoCache).then((r) => r.data);

/* ============================ Admin chat (impersonation) ============================ */
export const adminListUserChatThreads = (userId: string) =>
  API.get(`/admin/users/${userId}/chat-threads`, { params: nowTs(), headers: noCacheHeaders }).then((r) => r.data);

export const adminGetChatMessages = (threadId: string, limit = 200) =>
  API.get(`/admin/chat/threads/${threadId}/messages`, {
    params: { ...nowTs(), limit },
    headers: noCacheHeaders,
  }).then((r) => r.data);

/* ============================ Home activity ============================ */
export interface PasswordGenerationLogItem {
  _id: string;
  password: string;
  ipAddress: string;
  userAgent?: string;
  createdAt?: string;
}

export interface InternetSpeedLogItem {
  _id: string;
  downloadMbps: number;
  uploadMbps: number;
  unloadedLatencyMs?: number | null;
  downloadLoadedLatencyMs?: number | null;
  uploadLoadedLatencyMs?: number | null;
  connectionType?: string;
  ipAddress: string;
  userAgent?: string;
  createdAt?: string;
}

export const fetchPasswordGenerationLogs = async (limit = 200) => {
  const r = await API.get('/home-activity/password-generations', {
    params: { ...nowTs(), limit },
    headers: noCacheHeaders,
  });
  return unwrapList<PasswordGenerationLogItem>(r.data, ['items']);
};

export const fetchInternetSpeedLogs = async (limit = 200) => {
  const r = await API.get('/home-activity/internet-speed', {
    params: { ...nowTs(), limit },
    headers: noCacheHeaders,
  });
  return unwrapList<InternetSpeedLogItem>(r.data, ['items']);
};
